/**
 * How an Apply is announced: one toast, and a dialog when it was partial.
 * For Media Devoted | Created By Ciro Autiero
 *
 * Split out of FlowOptimizerLivePreviewContext, which is already well past the
 * file limit. It owns the dialog state so the context only has to render it.
 *
 * Tres Commas answers a held Apply (its own switch, the write guard) with 201
 * and `applied: false` — that is announced as "Nothing pushed", never as a
 * success. Voluum and Binom keep their announcement exactly as before.
 */

import { useCallback, useState } from 'react';
import { matchTracker, type TrackerId } from '@trafficpulse/shared';
import type { FlowOptimizerPreviewResult } from '@trafficpulse/shared';
import type { FlowOptimizerApplyReport } from '@trafficpulse/shared';

import { useToast } from '@/providers/ToastProvider';
import { drawerProfile } from '@/features/flow-optimizer/tracker/drawer-profile';

import { applyHeadline, isPartialApply } from './apply-report-summary';
import { unpushedReason, type UnpushedApplyResult } from './unpushed-reason';

export interface UseApplyOutcome {
  /** Non-null only after a PARTIAL apply — feeds ApplyReportDialog. */
  partialReport: FlowOptimizerApplyReport | null;
  announce: (result: FlowOptimizerPreviewResult | undefined) => void;
  dismiss: () => void;
}

type ApplyResult = UnpushedApplyResult;

/** Why this tracker's Apply pushed nothing (null for Voluum/Binom by design).
 *  Shared by the drawer Apply and the Scheduler's "Update now". */
export function applyHeldReason(provider: TrackerId, result: ApplyResult): string | null {
  return drawerProfile(provider).reportsUnpushed ? unpushedReason(result) : null;
}

/** True when an Apply result is in and it reached the tracker. */
export function isApplyPushed(
  provider: TrackerId,
  result: FlowOptimizerPreviewResult | null | undefined,
): boolean {
  return result != null && applyHeldReason(provider, result) === null;
}

function successCopy(provider: TrackerId): string {
  return matchTracker(provider, {
    voluum: () => "New weights pushed and the tracker's own optimization turned off.",
    binom: () => 'New weights pushed to the tracker.',
    'tres-commas': () => 'New weights pushed to the tracker.',
  });
}

export function useApplyOutcome(provider: TrackerId): UseApplyOutcome {
  const toast = useToast();
  const [partialReport, setPartialReport] =
    useState<FlowOptimizerApplyReport | null>(null);

  const announce = useCallback(
    (result: FlowOptimizerPreviewResult | undefined) => {
      const held = applyHeldReason(provider, result);
      if (held) {
        toast.warning('Nothing pushed', held);
        return;
      }
      const report = result?.applyReport;
      if (isPartialApply(report)) {
        // The weights DID reach the tracker — this is a partial, not a failure.
        // The dialog opens by itself because a toast cannot carry the
        // per-element detail, and that detail is the whole point here.
        toast.warning('Applied — some elements skipped', applyHeadline(report));
        setPartialReport(report);
        return;
      }
      toast.success('Optimization applied', successCopy(provider));
    },
    [toast, provider],
  );

  return {
    partialReport,
    announce,
    dismiss: useCallback(() => setPartialReport(null), []),
  };
}
